import React, { Fragment, useEffect, useState } from 'react';

import {
    Row, Col, Typography, Select, Avatar, Tag, Tabs, Badge, Empty, Spin, Card
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { getBirthdayEvents, getUpcomingEvents } from './redux/slice';

import '../../styles/pages/birthday-calendar.scss';

const { Title, Text } = Typography;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    label: dayjs().month(i).format('MMMM'),
    value: String(i + 1).padStart(2, '0'),
}));

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
    const y = dayjs().year() - 1 + i;
    return { label: String(y), value: String(y) };
});

const EventCard = ({ item, t }) => {
    const isBirthday = item.type === 'birthday';
    const color = isBirthday ? 'gold' : 'blue';
    const emoji = isBirthday ? '🎂' : '🏆';
    const ageNumber = isBirthday ? item.age : item.years;
    const ageLabel = isBirthday
        ? t('birthday_calendar_years_old')
        : t('birthday_calendar_years_service');
    const dateText = `${String(item.day).padStart(2, '0')}.${String(item.month).padStart(2, '0')}.${item.year}`;

    return (
        <Card
            size='small'
            className='birthday-event-card'
            style={{ borderLeft: `4px solid ${isBirthday ? '#faad14' : '#1677ff'}` }}
        >
            <Row align='middle' gutter={12}>
                <Col flex='none'>
                    <Avatar
                        src={item.profilePicture || null}
                        style={{ background: isBirthday ? '#fff7e6' : '#e6f4ff', color: isBirthday ? '#d48806' : '#0958d9' }}
                        size={48}
                    >
                        {!item.profilePicture && item.name?.charAt(0)}
                    </Avatar>
                </Col>
                <Col flex='auto'>
                    <Row justify='space-between' align='middle'>
                        <Col>
                            <Text strong>{item.name}</Text>
                            {item.abteilung && (
                                <Text type='secondary' style={{ marginLeft: 8, fontSize: 12 }}>
                                    {item.abteilung}
                                </Text>
                            )}
                            <br />
                            <Text type='secondary' style={{ fontSize: 12 }}>{emoji} {dateText}</Text>
                        </Col>
                        <Col>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    fontSize: 28,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: isBirthday ? '#d48806' : '#0958d9',
                                }}>
                                    {ageNumber}
                                </div>
                                <Tag color={color} style={{ marginTop: 4 }}>{ageLabel}</Tag>
                            </div>
                        </Col>
                    </Row>
                    {item.daysUntil !== undefined && (
                        <Text type='secondary' style={{ fontSize: 12 }}>
                            {item.daysUntil === 0
                                ? t('birthday_calendar_today')
                                : item.daysUntil === 1
                                    ? t('birthday_calendar_tomorrow')
                                    : `${t('birthday_calendar_in')} ${item.daysUntil} ${t('birthday_calendar_days')}`}
                        </Text>
                    )}
                </Col>
            </Row>
        </Card>
    );
};

const BirthdayCalendar = () => {
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const birthdayCalendar = useSelector(state => state.birthdayCalendar);
    const { t } = useTranslation();

    const [month, setMonth] = useState(dayjs().format('MM'));
    const [year, setYear] = useState(dayjs().format('YYYY'));

    useEffect(() => {
        if (auth?.decodeToken?.organizer) {
            dispatch(getBirthdayEvents({
                organizer: auth.decodeToken.organizer,
                month,
                year,
            }));
            dispatch(getUpcomingEvents({
                organizer: auth.decodeToken.organizer,
                days: 30,
            }));
        }
    }, [auth?.decodeToken?.organizer, month, year]);

    const { birthdays, anniversaries, upcoming, loading } = birthdayCalendar;

    const renderList = (items) => {
        if (!items || items.length === 0) {
            return <Empty description={t('birthday_calendar_no_events')} />;
        }
        return (
            <Row gutter={[0, 12]}>
                {items.map((item) => (
                    <Col xs={24} key={`${item._id}-${item.type}`}>
                        <EventCard item={item} t={t} />
                    </Col>
                ))}
            </Row>
        );
    };

    return (
        <Fragment>
            <Spin spinning={loading}>
                <Row gutter={[16, 16]}>
                    {/* Header */}
                    <Col xs={24}>
                        <Row
                            justify='space-between'
                            align='middle'
                            className='padding-1 rounded-1'
                            gutter={[16, 16]}
                        >
                            <Col flex='none'>
                                <Title level={4} type='success'>
                                    {t('birthday_calendar_title')}
                                </Title>
                            </Col>
                            <Col>
                                <Row gutter={8}>
                                    <Col>
                                        <Select
                                            value={month}
                                            onChange={setMonth}
                                            options={MONTH_OPTIONS}
                                            style={{ width: 140 }}
                                        />
                                    </Col>
                                    <Col>
                                        <Select
                                            value={year}
                                            onChange={setYear}
                                            options={YEAR_OPTIONS}
                                            style={{ width: 100 }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>

                    {/* Upcoming banner */}
                    <Col xs={24}>
                        <Card
                            className='bg-white rounded-2'
                            title={
                                <Title level={5} type='success' style={{ margin: 0 }}>
                                    🔔 {t('birthday_calendar_upcoming_30')}
                                </Title>
                            }
                            size='small'
                        >
                            {renderList(upcoming)}
                        </Card>
                    </Col>

                    {/* Monthly tabs */}
                    <Col xs={24}>
                        <div className='bg-white rounded-2'>
                            <Tabs
                                type='card'
                                size='large'
                                className='padding-2'
                                items={[
                                    {
                                        label: (
                                            <span>
                                                🎂 {t('birthday_calendar_birthdays')}
                                                <Badge
                                                    count={birthdays.length}
                                                    style={{ marginLeft: 8, background: '#faad14' }}
                                                />
                                            </span>
                                        ),
                                        key: '1',
                                        children: renderList(birthdays),
                                    },
                                    {
                                        label: (
                                            <span>
                                                🏆 {t('birthday_calendar_anniversaries')}
                                                <Badge
                                                    count={anniversaries.length}
                                                    style={{ marginLeft: 8, background: '#1677ff' }}
                                                />
                                            </span>
                                        ),
                                        key: '2',
                                        children: renderList(anniversaries),
                                    },
                                ]}
                            />
                        </div>
                    </Col>
                </Row>
            </Spin>
        </Fragment>
    );
};

export default BirthdayCalendar;
